<template>
  <div class="data-page">
    <!-- Индикатор сохраненного состояния -->
    <StateIndicator
      :has-state="hasActiveFilters || hasSavedState"
      title="Настройки сохранены!"
      message="Ваши фильтры, сортировка и настройки таблицы сохранены."
      @reset="resetTableState"
    />

    <div class="page-header mb-4">
      <div class="flex align-items-center justify-content-between">
        <div>
          <h1 class="text-900 mb-1">Управление очками</h1>
          <p class="text-600 mb-0">Просмотр, редактирование и управление базой данных очков</p>
        </div>
        <div class="flex align-items-center gap-3">
          <ProxyStatusWidget />
          <div>
            <Button
              label="Действия"
              icon="pi pi-bars"
              class="p-button-primary"
              @click="toggleActionsMenu"
            />
            <Menu ref="actionsMenu" :model="actionMenuItems" :popup="true" />
          </div>
        </div>
      </div>
    </div>

    <!-- Компонент редактора данных -->
    <DataEditor 
      ref="dataEditor" 
      @show-enrichment="showEnrichmentDialog = true"
    />

    <!-- Диалог добавления товара -->
    <Dialog
      v-model:visible="showAddDialog"
      modal
      header="Добавление новых очков"
      :style="{ width: '50rem' }"
      :closable="true"
    >
      <div class="p-fluid">
        <div class="grid">
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Артикул (Part Number) *</label>
            <InputText v-model="newProduct.part_number" :class="{ 'p-invalid': errors.part_number }" />
            <small v-if="errors.part_number" class="p-error">{{ errors.part_number }}</small>
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Название</label>
            <InputText v-model="newProduct.part_name" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Бренд</label>
            <InputText v-model="newProduct.brand" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Категория</label>
            <InputText v-model="newProduct.category" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Цвет</label>
            <InputText v-model="newProduct.color" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Код цвета</label>
            <InputText v-model="newProduct.color_code" placeholder="Например: 2268, BRU0A7, 03-115" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Размер</label>
            <InputText v-model="newProduct.size" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Год</label>
            <InputNumber v-model="newProduct.year" :min="1900" :max="2030" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Переносица (Bridge)</label>
            <InputNumber v-model="newProduct.bridge" suffix=" мм" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Длина заушника (Temple)</label>
            <InputNumber v-model="newProduct.temple_length" suffix=" мм" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">Высота линзы</label>
            <InputNumber v-model="newProduct.lens_height" suffix=" мм" />
          </div>
          <div class="col-12 md:col-6">
            <label class="block text-900 font-medium mb-2">РРЦ</label>
            <InputNumber v-model="newProduct.rrp" mode="decimal" :minFractionDigits="2" :maxFractionDigits="2" />
          </div>
          <div class="col-12">
            <label class="block text-900 font-medium mb-2">Изображения (URL через запятую)</label>
            <Textarea v-model="newProduct.images" :autoResize="true" rows="3" placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg" />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="cancelAdd"
        />
        <Button
          label="Добавить"
          icon="pi pi-plus"
          class="p-button-primary"
          :loading="adding"
          @click="addProduct"
        />
      </template>
    </Dialog>

    <!-- Диалог экспорта -->
    <Dialog
      v-model:visible="showExportDialog"
      modal
      header="Экспорт данных"
      :style="{ width: '40rem' }"
      :closable="true"
    >
      <div class="p-fluid">
        <div class="field mb-4">
          <label class="block text-900 font-medium mb-2">Формат экспорта</label>
          <div class="flex gap-3">
            <div class="flex align-items-center">
              <RadioButton v-model="exportFormat" inputId="excel" value="excel" />
              <label for="excel" class="ml-2">Excel (.xlsx)</label>
            </div>
            <div class="flex align-items-center">
              <RadioButton v-model="exportFormat" inputId="csv" value="csv" />
              <label for="csv" class="ml-2">CSV</label>
            </div>
          </div>
        </div>

        <div class="field mb-4">
          <label class="block text-900 font-medium mb-2">Выберите колонки для экспорта</label>
          <div class="grid">
            <div
              v-for="column in availableColumns"
              :key="column.value"
              class="col-12 md:col-6"
            >
              <div class="flex align-items-center">
                <Checkbox
                  v-model="selectedColumns"
                  :inputId="column.value"
                  :value="column.value"
                />
                <label :for="column.value" class="ml-2">{{ column.label }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="block text-900 font-medium mb-2">Фильтры</label>
          <div class="grid">
            <div class="col-12 md:col-6">
              <Dropdown
                v-model="exportFilters.brand"
                :options="brandOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Бренд"
                :showClear="true"
                class="w-full"
              />
            </div>
            <div class="col-12 md:col-6">
              <Dropdown
                v-model="exportFilters.category"
                :options="categoryOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Категория"
                :showClear="true"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="showExportDialog = false"
        />
        <Button
          label="Экспортировать"
          icon="pi pi-download"
          class="p-button-primary"
          :loading="exporting"
          @click="performExport"
        />
      </template>
    </Dialog>

    <!-- Диалог настроек маппинга -->
    <MappingSettings
      v-model="showMappingSettings"
      @settings-updated="onMappingSettingsUpdated"
    />

    <!-- Диалог исключений состояния -->
    <ConditionExceptionsDialog v-model="showConditionExceptionsDialog" />

    <!-- Диалог словаря «Статус на сайте → код состояния» -->
    <SiteStatusMappingsDialog v-model="showSiteStatusMappingsDialog" />
    <BrandMappingDialog v-model:visible="showBrandMappingDialog" />

    <!-- Диалог импорта бренд → производитель -->
    <Dialog
      v-model:visible="showBrandProducerDialog"
      modal
      header="Импорт справочника бренд → производитель"
      :style="{ width: '32rem' }"
      :closable="true"
      @hide="resetBrandProducerState"
    >
      <div class="p-fluid">
        <div class="field mb-4">
          <label class="block text-900 font-medium mb-2">Файл Excel</label>
          <input
            ref="brandProducerInput"
            type="file"
            accept=".xlsx,.xls"
            class="w-full"
            @change="onBrandProducerFileChange"
          />
          <small class="text-500 block mt-2">
            Обязательные колонки: «Общие: Бренд», «Общие: Производитель».
          </small>
          <div v-if="brandProducerFileName" class="mt-2">
            <i class="pi pi-file-excel text-green-500 mr-2"></i>
            <span class="text-700">{{ brandProducerFileName }}</span>
          </div>
        </div>

        <div class="field-checkbox">
          <Checkbox
            inputId="brandProducerOverwrite"
            v-model="brandProducerOverwrite"
            :binary="true"
          />
          <label for="brandProducerOverwrite" class="ml-2">
            Перезаписать существующие значения
          </label>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeBrandProducerDialog"
        />
        <Button
          label="Импортировать"
          icon="pi pi-upload"
          class="p-button-primary"
          :disabled="!brandProducerFile"
          :loading="brandProducerUploading"
          @click="uploadBrandProducer"
        />
      </template>
    </Dialog>

    <!-- Диалог подтверждения восстановления БД -->
    <Dialog
      v-model:visible="showRestoreDialog"
      modal
      header="Подтверждение восстановления"
      :style="{ width: '30rem' }"
      :closable="true"
    >
      <div class="p-fluid">
        <p class="text-900 mb-4">
          Вы уверены, что хотите восстановить базу данных из последнего бэкапа?
          Все текущие изменения будут потеряны.
        </p>
        <div class="flex justify-content-end gap-2">
          <Button
            label="Отмена"
            icon="pi pi-times"
            class="p-button-text"
            @click="showRestoreDialog = false"
          />
          <Button
            label="Восстановить"
            icon="pi pi-refresh"
            class="p-button-danger"
            @click="performRestore"
          />
        </div>
      </div>
    </Dialog>

    <!-- Модальное окно отчетов -->
    <ReportsModal v-model:visible="showReportsModal" />

    <!-- Диалог обогащения данных -->
    <ProductEnrichmentDialog
      v-model:visible="showEnrichmentDialog"
      @enrichment-completed="onEnrichmentCompleted"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useDataStore } from '../stores/dataStore'
import { useProductSchema } from '../composables/useProductSchema'
import StateIndicator from '../components/StateIndicator.vue'
import Card from 'primevue/card'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import RadioButton from 'primevue/radiobutton'
import Checkbox from 'primevue/checkbox'
import Dropdown from 'primevue/dropdown'
import Menu from 'primevue/menu'
import DataEditor from '../components/DataEditor.vue'
import MappingSettings from '../components/MappingSettings.vue'
import ReportsModal from '../components/ReportsModal.vue'
import ProductEnrichmentDialog from '../components/ProductEnrichmentDialog.vue'
import ConditionExceptionsDialog from '../components/ConditionExceptionsDialog.vue'
import SiteStatusMappingsDialog from '../components/SiteStatusMappingsDialog.vue'
import BrandMappingDialog from '../components/BrandMappingDialog.vue'
import ProxyStatusWidget from '../components/ProxyStatusWidget.vue'
import apiService from '../services/apiService'

export default {
  name: 'DataPage',
  components: {
    Card,
    Button,
    Dialog,
    InputText,
    InputNumber,
    Textarea,
    RadioButton,
    Checkbox,
    Dropdown,
    DataEditor,
    Menu,
    MappingSettings,
    ReportsModal,
    ProductEnrichmentDialog,
    ConditionExceptionsDialog,
    SiteStatusMappingsDialog,
    BrandMappingDialog,
    StateIndicator,
    ProxyStatusWidget
  },
  setup() {
    const router = useRouter()
    const toast = useToast()
    const productSchema = useProductSchema()
    const dataEditor = ref()
    
    // Интеграция с dataStore
    const dataStore = useDataStore()
    const {
      showAddDialog,
      showExportDialog,
      showMappingSettings,
      showBrandProducerDialog,
      showRestoreDialog,
      showReportsModal,
      showConditionExceptionsDialog,
      showSiteStatusMappingsDialog,
      hasActiveFilters
    } = storeToRefs(dataStore)

    const showEnrichmentDialog = ref(false)
    const showBrandMappingDialog = ref(false)
    const actionsMenu = ref()

    const actionMenuItems = ref([
      { label: 'Данные', items: [
        { label: 'Добавить очки', icon: 'pi pi-plus', command: () => { showAddDialog.value = true } },
        { label: 'Массовое редактирование', icon: 'pi pi-pencil', command: () => { if (dataEditor.value) dataEditor.value.showBulkEditDialog = true } },
        { label: 'Поиск данных', icon: 'pi pi-search-plus', command: () => { showEnrichmentDialog.value = true } },
        { label: 'Обновить', icon: 'pi pi-refresh', command: () => { if (dataEditor.value) dataEditor.value.showSyncDialog = true } }
      ]},
      { label: 'Импорт / Экспорт', items: [
        { label: 'Импорт из Excel', icon: 'pi pi-upload', command: () => { router.push('/files') } },
        { label: 'Импорт брендов', icon: 'pi pi-briefcase', command: () => { showBrandProducerDialog.value = true } },
        { label: 'Синхронизация с Google Sheets', icon: 'pi pi-google', command: () => { if (dataEditor.value) dataEditor.value.showSheetsSyncDialog = true } },
        { label: 'Конвертер UPC - ТНВЭД', icon: 'pi pi-file-excel', command: () => { if (dataEditor.value) dataEditor.value.showUpcConverterDialog = true } },
        { label: 'Таможенный документ', icon: 'pi pi-file-export', command: () => { if (dataEditor.value) dataEditor.value.showShipmentExportDialog = true } }
      ]},
      { label: 'Администрирование', items: [
        { label: 'Отчеты', icon: 'pi pi-chart-bar', command: () => { showReportsModal.value = true } },
        { label: 'Настройки маппинга', icon: 'pi pi-cog', command: () => { showMappingSettings.value = true } },
        { label: 'Исключения состояния', icon: 'pi pi-ban', command: () => { showConditionExceptionsDialog.value = true } },
        { label: 'Статусы → код состояния', icon: 'pi pi-sliders-h', command: () => { showSiteStatusMappingsDialog.value = true } },
        { label: 'Маппинг брендов', icon: 'pi pi-tags', command: () => { showBrandMappingDialog.value = true } },
        { label: 'Восстановить БД', icon: 'pi pi-refresh', command: () => { showRestoreDialog.value = true } }
      ]}
    ])

    const toggleActionsMenu = (event) => {
      actionsMenu.value.toggle(event)
    }
    
    // Локальные refs
    const adding = ref(false)
    const exporting = ref(false)
    const brandProducerUploading = ref(false)
    const brandProducerOverwrite = ref(false)
    const brandProducerFile = ref(null)
    const brandProducerFileName = ref('')
    const brandProducerInput = ref(null)
    
    // Проверка наличия сохраненного состояния
    const hasSavedState = computed(() => {
      return dataStore.first > 0 || 
             dataStore.sortField !== null ||
             dataStore.visibleColumns.length < 6
    })

    const newProduct = reactive({
      part_number: '',
      part_name: '',
      brand: '',
      category: '',
      color: '',
      color_code: '',
      size: '',
      year: null,
      bridge: null,
      temple_length: null,
      lens_height: null,
      rrp: null,
      images: ''
    })

    const errors = reactive({})

    const exportFormat = ref('excel')

    // Список и предвыбор колонок генерируются из единой схемы
    // (productSchemaStore → /products/schema). См. composable useProductSchema.
    const availableColumns = computed(() =>
      [...productSchema.exportable.value]
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((f) => ({ label: f.display_name, value: f.key })),
    )
    const selectedColumns = ref([])
    // Когда схема прогрузилась — выбираем все колонки по умолчанию.
    watch(
      availableColumns,
      (cols) => {
        if (selectedColumns.value.length === 0 && cols.length > 0) {
          selectedColumns.value = cols.map((c) => c.value)
        }
      },
      { immediate: true },
    )

    const exportFilters = reactive({
      brand: null,
      category: null
    })

    const brandOptions = ref([])
    const categoryOptions = ref([])

    const onBrandProducerFileChange = (event) => {
      const file = event.target?.files?.[0] || null
      brandProducerFile.value = file
      brandProducerFileName.value = file ? file.name : ''
    }

    const resetBrandProducerState = () => {
      brandProducerFile.value = null
      brandProducerFileName.value = ''
      brandProducerOverwrite.value = false
      if (brandProducerInput.value) {
        brandProducerInput.value.value = ''
      }
    }

    const closeBrandProducerDialog = () => {
      showBrandProducerDialog.value = false
    }

    const uploadBrandProducer = async () => {
      if (!brandProducerFile.value) {
        toast.add({
          severity: 'warn',
          summary: 'Файл не выбран',
          detail: 'Пожалуйста, выберите Excel-файл со справочником брендов',
          life: 3000
        })
        return
      }

      brandProducerUploading.value = true
      try {
        await apiService.importBrandProducers(brandProducerFile.value, brandProducerOverwrite.value)
        toast.add({
          severity: 'success',
          summary: 'Импорт завершён',
          detail: 'Справочник бренд → производитель успешно обновлён',
          life: 3000
        })
        closeBrandProducerDialog()
      } catch (error) {
        const detail = error?.response?.data?.detail || 'Не удалось импортировать файл'
        toast.add({
          severity: 'error',
          summary: 'Ошибка импорта',
          detail,
          life: 5000
        })
      } finally {
        brandProducerUploading.value = false
      }
    }

    const goToFiles = () => {
      router.push('/files')
    }
    
    const resetTableState = () => {
      dataStore.resetState()
      toast.add({
        severity: 'success',
        summary: 'Настройки сброшены',
        detail: 'Все фильтры, сортировка и настройки таблицы очищены',
        life: 3000
      })
      // Перезагружаем данные в таблице
      if (dataEditor.value) {
        dataEditor.value.loadProducts()
      }
    }

    const validateProduct = () => {
  errors.part_number = !newProduct.part_number.trim() ? 'Артикул (Part Number) обязателен' : ''
      return !Object.values(errors).some(error => error)
    }

    const addProduct = async () => {
      if (!validateProduct()) return

      adding.value = true
      try {
        // Очистка от пустых значений
        const productData = {}
        Object.keys(newProduct).forEach(key => {
          if (newProduct[key] !== null && newProduct[key] !== '') {
            productData[key] = newProduct[key]
          }
        })

        await apiService.createProduct(productData)
        showAddDialog.value = false
        resetNewProduct()

        // Обновляем таблицу
        if (dataEditor.value) {
          dataEditor.value.loadProducts()
        }

        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Товар добавлен',
          life: 3000
        })
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось добавить товар',
          life: 3000
        })
      } finally {
        adding.value = false
      }
    }

    const cancelAdd = () => {
      showAddDialog.value = false
      resetNewProduct()
    }

    const resetNewProduct = () => {
      Object.keys(newProduct).forEach(key => {
        if (typeof newProduct[key] === 'string') {
          newProduct[key] = ''
        } else {
          newProduct[key] = null
        }
      })
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })
    }

    const performExport = async () => {
      if (selectedColumns.value.length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Предупреждение',
          detail: 'Выберите хотя бы одну колонку',
          life: 3000
        })
        return
      }

      exporting.value = true
      try {
        // TODO: Реализовать экспорт через API
        const exportData = {
          format: exportFormat.value,
          columns: selectedColumns.value,
          filters: exportFilters
        }

        toast.add({
          severity: 'info',
          summary: 'Информация',
          detail: 'Функционал экспорта будет реализован в ближайшее время',
          life: 3000
        })

        showExportDialog.value = false
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось выполнить экспорт',
          life: 3000
        })
      } finally {
        exporting.value = false
      }
    }

    const loadFilterOptions = async () => {
      try {
        const response = await apiService.getProducts({ limit: 1000 })
        const brands = new Set()
        const categories = new Set()

        response.data.forEach(product => {
          if (product.brand) brands.add(product.brand)
          if (product.category) categories.add(product.category)
        })

        brandOptions.value = Array.from(brands).map(item => ({ label: item, value: item }))
        categoryOptions.value = Array.from(categories).map(item => ({ label: item, value: item }))
      } catch (error) {
        console.error('Ошибка загрузки опций фильтров:', error)
      }
    }

    const onMappingSettingsUpdated = () => {
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Настройки маппинга обновлены. Они будут использоваться при следующей загрузке Excel файлов.',
        life: 5000
      })
    }

    const performRestore = async () => {
      try {
        const response = await apiService.restoreDatabase()
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: response.data.message,
          life: 5000
        })
        showRestoreDialog.value = false
        // Перезагрузка страницы для обновления данных
        window.location.reload()
      } catch (error) {
        console.error('Ошибка восстановления:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось восстановить базу данных',
          life: 5000
        })
      }
    }

    const onEnrichmentCompleted = (stats) => {
      // Обновляем таблицу после завершения обогащения
      if (dataEditor.value) {
        dataEditor.value.loadProducts()
      }

      toast.add({
        severity: 'success',
        summary: 'Обогащение завершено',
        detail: `Обновлено товаров: ${stats.updated}, Пропущено: ${stats.skipped}, Ошибок: ${stats.failed}`,
        life: 7000
      })
    }

    onMounted(() => {
      loadFilterOptions()
    })

    return {
      dataEditor,
      showAddDialog,
      showExportDialog,
      showMappingSettings,
      showBrandProducerDialog,
      showRestoreDialog,
      showReportsModal,
      showConditionExceptionsDialog,
      showSiteStatusMappingsDialog,
      adding,
      exporting,
      brandProducerUploading,
      brandProducerOverwrite,
      brandProducerFile,
      brandProducerFileName,
      brandProducerInput,
      newProduct,
      errors,
      exportFormat,
      selectedColumns,
      exportFilters,
      availableColumns,
      brandOptions,
      categoryOptions,
      onBrandProducerFileChange,
      uploadBrandProducer,
      closeBrandProducerDialog,
      resetBrandProducerState,
      goToFiles,
      actionsMenu,
      actionMenuItems,
      toggleActionsMenu,
      addProduct,
      cancelAdd,
      performExport,
      performRestore,
      onMappingSettingsUpdated,
      // Новые из store
      hasActiveFilters,
      hasSavedState,
      resetTableState,
      // Обогащение данных
      showEnrichmentDialog,
      showBrandMappingDialog,
      onEnrichmentCompleted
    }
  }
}
</script>

<style scoped>
.data-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}

.page-header {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.field {
  margin-bottom: 1.5rem;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .data-page {
    padding: 0.5rem;
  }
  .page-header {
    padding: 1rem;
  }
  .page-header p {
    display: none;
  }
}
</style>
